// ============================================================================
// IMPORTS
// ============================================================================

// React imports
import { createRoot, Root } from 'react-dom/client';

// Remirror/ProseMirror imports
import { CommandFunction, NodeExtension, ApplySchemaAttributes, NodeSpecOverride } from "@remirror/core";
import { NodeView, EditorView } from 'prosemirror-view';
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { NodeSelection } from 'prosemirror-state';

// Component imports
import VimeoVideo from "../../../../VimeoVideo";
import BasicVideoPlayer from "../../../../BasicVideoPlayer";
import { VideoTransitionWrapper } from "./components/VideoTransitionWrapper";
import { ResizeHandle, ResizeIcon } from "./components/VideoResizer/styles";
import Icon from "../../../../Icon";
import resizeIcon from "../../../../../assets/icons/resize.svg";

// Helper imports
import { getVimeoVideoIdFromUrl, getYouTubeUrl } from "../../../../../helpers/you-tube";

// Type imports
import { AttachedFile } from "../../../../../types/attached-file";
import { VideoLoader } from './components/VideoLoader';

// Style imports


// ============================================================================
// CUSTOM VIDEO NODE VIEW (EDIT MODE)
// ============================================================================

/**
 * NodeView para videos en modo edición
 * Maneja la renderización de videos de YouTube, Vimeo y archivos locales
 * Permite selección del nodo para edición
 */
export class CustomVideoNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;
  private resizeRoot: Root | null = null;
  private videoWidth: number = 640;
  private videoHeight: number = 360;
  private isPortrait: boolean = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private originalRatio = 16 / 9; // default ratio

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private getPos: () => number,
    private mediaFilesMap?: Record<number, AttachedFile>,
  ) {
    // Crear elemento DOM principal
    this.dom = document.createElement("div");
    this.dom.className = "remirror-video-wrapper";

    // Crear contenedor para React
    this.reactContainer = document.createElement("div");
    this.reactContainer.style.overflow = "hidden";
    this.reactContainer.style.width = "100%";
    this.reactContainer.style.height = "100%";

    this.dom.appendChild(this.reactContainer);

    // Inicializar dimensiones basadas en el tipo de video
    this.initializeVideoDimensions();

    // Renderizar contenido inicial
    this.renderContent();
    this.handleSelection();

    // Add resize handler for all video types (YouTube, Vimeo, and local files)
    this.addResizeHandle();

    // Marcar como seleccionado si corresponde
    const isSelected =
      this.view.state.selection instanceof NodeSelection &&
      this.view.state.selection.from === this.getPos();
    if (isSelected) {
      this.dom.setAttribute("data-selected", "true");
    }
  }

  /**
   * Agrega el resize handle usando React
   */
  private addResizeHandle() {
    const resizeHandleContainer = document.createElement("div");
    resizeHandleContainer.style.position = "absolute";
    resizeHandleContainer.style.bottom = "20px";
    resizeHandleContainer.style.right = "20px";
    resizeHandleContainer.style.zIndex = "10";

    this.dom.appendChild(resizeHandleContainer);

    // Crear root para el resize handle
    this.resizeRoot = createRoot(resizeHandleContainer);

    // Renderizar el resize handle con estilos
    this.resizeRoot.render(
      <ResizeHandle
        onMouseDown={this.initResize}
        isResizing={false}
      >
        <Icon icon={resizeIcon} color="white" size="16px" />
      </ResizeHandle>
    );
  }

  /**
   * Inicializa las dimensiones del video basadas en el tipo
   */
  private initializeVideoDimensions() {
    const provider = this.node.attrs.provider;


    // Determinar si es portrait basado en el provider o atributos
    this.isPortrait = this.node.attrs.isPortrait || false;

    // Si ya tenemos dimensiones guardadas en el nodo, usarlas
    if (this.node.attrs.width) {
      this.videoWidth = this.node.attrs.width;
      // Para videos verticales, height puede ser null, usar el valor calculado
      if (this.node.attrs.height !== null && this.node.attrs.height !== undefined) {
        this.videoHeight = this.node.attrs.height;
      } else {
        // Para videos verticales, calcular height basado en el aspect ratio
        this.videoHeight = this.videoWidth * (9 / 16); // Aspect ratio 9:16 para portrait
      }
      this.originalRatio = this.videoWidth / this.videoHeight;

    } else {
      // Usar dimensiones por defecto basadas en la orientación
      if (this.isPortrait) {
        // Video portrait: ancho máximo 320px
        this.videoWidth = 320;
        this.videoHeight = this.videoWidth * (9 / 16); // Aspect ratio 9:16
        this.originalRatio = 9 / 16;
      } else {
        // Video landscape: ancho máximo 640px
        this.videoWidth = 640;
        this.videoHeight = this.videoWidth / (16 / 9); // Aspect ratio 16:9
        this.originalRatio = 16 / 9;
      }

    }

    // Aplicar dimensiones al contenedor
    this.updateContainerDimensions();
  }

  /**
   * Actualiza las dimensiones del contenedor
   */
  private updateContainerDimensions() {
    this.dom.style.width = `${this.videoWidth}px`;
    // Para videos verticales, usar height: auto para que se calcule automáticamente
    this.dom.style.height = "auto";
  }

  /**
   * Inicializa el resize del video (como en el código anterior)
   */
  initResize = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();


    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startWidth = this.dom.offsetWidth;
    this.startHeight = this.dom.offsetHeight;

    document.addEventListener("mousemove", this.doResize);
    document.addEventListener("mouseup", this.stopResize);
    document.addEventListener("mouseleave", this.stopResize);
    window.addEventListener("blur", this.stopResize);
    document.body.style.userSelect = "none";

    // Add resize state class
    this.dom.classList.add("resizing");
  }

  /**
   * Maneja el resize en tiempo real (como en el código anterior)
   */
  doResize = (e: MouseEvent) => {
    // Calculate the change in horizontal position
    const dx = e.clientX - this.startX;

    // Calculate new width based on horizontal movement
    const newWidth = Math.max(240, this.startWidth + dx);

    // Calculate new height maintaining the original ratio
    const newHeight = Math.round(newWidth / this.originalRatio);

    // Apply new dimensions
    this.dom.style.width = `${newWidth}px`;
    this.dom.style.height = "auto";
    // this.dom.style.aspectRatio = `${newWidth} / ${newHeight}`;

    // Update aspect ratio in container
    // if (this.reactContainer) {
    //   this.reactContainer.style.aspectRatio = `${this.originalRatio}`;
    // }

    // Update internal dimensions
    this.videoWidth = newWidth;
    this.videoHeight = newHeight;
  }

  /**
   * Finaliza el resize y actualiza el nodo (como en el código anterior)
   */
  stopResize = () => {
    document.removeEventListener("mousemove", this.doResize);
    document.removeEventListener("mouseup", this.stopResize);
    document.removeEventListener("mouseleave", this.stopResize);
    window.removeEventListener("blur", this.stopResize);
    document.body.style.userSelect = "auto";

    // Remove resize state class
    this.dom.classList.remove("resizing");

    // Update dimensions for all video types
    const pos = this.getPos();
    const newWidth = this.dom.offsetWidth;
    const newHeight = this.dom.offsetHeight;


    // Update the node with the new dimensions
    const tr = this.view.state.tr.setNodeMarkup(pos, undefined, {
      ...this.node.attrs,
      width: newWidth as number,
      height: this.isPortrait ? null : newHeight as number,
    });

    this.view.dispatch(tr);


    // Restore focus to the editor
    this.view.focus();
  }

  /**
   * Actualiza el nodo cuando cambia su contenido
   */
  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) {
      return false;
    }


    this.node = node;

    // Actualizar dimensiones si han cambiado
    if (node.attrs.width) {
      this.videoWidth = node.attrs.width;
      // Para videos verticales, height puede ser null, usar el valor calculado
      if (node.attrs.height !== null && node.attrs.height !== undefined) {
        this.videoHeight = node.attrs.height;
      } else {
        // Para videos verticales, calcular height basado en el aspect ratio
        this.videoHeight = this.videoWidth * (9 / 16); // Aspect ratio 9:16 para portrait
      }
      // NO recalcular originalRatio aquí - mantener el original
      // this.originalRatio = this.videoWidth / this.videoHeight; // REMOVIDO
      this.updateContainerDimensions();

      // Forzar re-render del contenido para actualizar VideoResizer con nuevas props
      this.renderContent();
    } else {
      // Si no hay cambios en las dimensiones, solo re-renderizar el contenido
      this.renderContent();
    }

    this.handleSelection();

    return true;
  }

  /**
   * Marca el nodo como seleccionado
   */
  selectNode() {
    this.dom.setAttribute("data-selected", "true");
  }

  /**
   * Desmarca el nodo como seleccionado
   */
  deselectNode() {
    this.dom.removeAttribute("data-selected");
  }

  /**
   * Limpia recursos al destruir el nodo
   */
  destroy() {
    if (this.root) {
      this.root.unmount();
    }
    if (this.resizeRoot) {
      this.resizeRoot.unmount();
    }
  }

  /**
   * Renderiza el contenido del video según su estado y proveedor
   */
  renderContent() {
    const {
      src,
      filename,
      filesize,
      "data-uploading": uploading,
      "data-progress": progress
    } = this.node.attrs;

    const isUploading = uploading === true || uploading === "true" || uploading === "1";

    // Solo limpiar si es necesario (evitar pestañeos)
    if (!this.root) {
      this.root = createRoot(this.reactContainer);
    }

    // Eliminar badge de progreso existente
    const existingBadge = this.dom.querySelector(".progress-badge");
    if (existingBadge) {
      existingBadge.remove();
    }

    // Renderizar según el estado
    if (isUploading) {
      this.renderUploadingState(filename, progress);
    } else {
      this.renderVideoPlayer(src);
    }
  }

  /**
   * Renderiza el estado de subida del video
   */
  private renderUploadingState(filename: string, progress: number) {
    this.root!.render(
      <VideoLoader filename={filename} progress={progress} />
    );
  }

  /**
   * Renderiza el reproductor de video según el proveedor
   */
  private renderVideoPlayer(src: string) {
    const provider = this.node.attrs.provider;

    switch (provider) {
      case 'youtube':
        this.renderYouTubePlayer(src);
        break;
      case 'vimeo':
        this.renderVimeoPlayer(src);
        break;
      default:
        this.renderLocalVideoPlayer(src);
        break;
    }
  }

  /**
   * Renderiza reproductor de YouTube
   */
  private renderYouTubePlayer(src: string) {
    const youtubeSrc = getYouTubeUrl(src);

    this.root!.render(
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <iframe
          src={youtubeSrc}
          allowFullScreen
          style={{
            display: "block",
            border: "none",
            maxWidth: "100%",
            width: "100%",
            height: "100%",
            aspectRatio: "16/9",
            borderRadius: "8px",
          }}
        />
      </div>
    );
  }

  /**
   * Renderiza reproductor de Vimeo
   */
  private renderVimeoPlayer(src: string) {
    this.root!.render(
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <VimeoVideo
          url={src}
          showLink={false}
          isPreview={false}
        />
      </div>
    );
  }

  /**
   * Renderiza reproductor de video local
   */
  private renderLocalVideoPlayer(src: string) {
    const finalSrc = this.mediaFilesMap?.[this.node.attrs.media_file_id]?.signed_url || src;

    this.root!.render(
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <BasicVideoPlayer
          videoUrl={finalSrc}
          blockDownload={true}
          isOwner={false}
          mode="edit"
          editable={false}
          onLoadedMetadata={this.handleVideoMetadataLoaded}
        />
      </div>
    );
  }

  /**
   * Maneja la carga de metadatos del video para detectar orientación
   */
  handleVideoMetadataLoaded = (videoEl: HTMLVideoElement) => {
    const { videoWidth, videoHeight } = videoEl;
    const isVertical = videoHeight > videoWidth;


    // Actualizar el aspect ratio original basado en las dimensiones reales del video
    this.originalRatio = videoWidth / videoHeight;

    // Solo actualizar si la orientación ha cambiado
    if (this.isPortrait !== isVertical) {

      this.isPortrait = isVertical;

      // Actualizar dimensiones basadas en la orientación detectada
      if (this.isPortrait) {
        this.videoWidth = 320;
        this.videoHeight = this.videoWidth * (9 / 16);
      } else {
        this.videoWidth = 640;
        this.videoHeight = this.videoWidth / (16 / 9);
      }


      this.updateContainerDimensions();

      // Actualizar el nodo con la orientación detectada
      // Para videos verticales, no guardar height en el nodo ya que usamos auto
      const tr = this.view.state.tr.setNodeMarkup(this.getPos(), undefined, {
        ...this.node.attrs,
        isPortrait: this.isPortrait,
        width: this.videoWidth,
        height: this.isPortrait ? null : this.videoHeight
      });
      this.view.dispatch(tr);

      // Re-renderizar el contenido con la nueva orientación
      this.renderContent();
    } else {
    }
  };

  /**
   * Maneja la selección del nodo al hacer click
   */
  handleSelection = () => {
    this.dom.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Seleccionar el nodo completo
      const pos = this.getPos();
      const { tr } = this.view.state;
      const nodeSelection = NodeSelection.create(this.view.state.doc, pos);
      tr.setSelection(nodeSelection);
      this.view.dispatch(tr);
    });
  }
}

// ============================================================================
// PREVIEW VIDEO NODE VIEW (READ-ONLY MODE)
// ============================================================================

/**
 * NodeView para videos en modo solo lectura (preview)
 * Versión simplificada sin funcionalidades de edición
 */
export class PreviewVideoNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;
  private videoWidth: number = 640;
  private videoHeight: number = 360;
  private isPortrait: boolean = false;

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    // Crear elemento DOM principal
    this.dom = document.createElement("div");
    this.dom.className = "remirror-video-wrapper";

    // Bloquear arrastre manual
    this.dom.draggable = false;
    this.dom.addEventListener("dragstart", (e) => e.preventDefault());

    // Crear contenedor para React
    this.reactContainer = document.createElement("div");
    this.reactContainer.className = "remirror-video-wrapper-container";
    this.dom.appendChild(this.reactContainer);

    // Inicializar dimensiones del video
    this.initializeVideoDimensions();

    // Renderizar contenido inicial
    this.renderContent();
  }

  /**
   * Calcula el aspect ratio dinámicamente basado en las dimensiones del video
   */
  private getDynamicAspectRatio(): string {
    if (this.videoWidth && this.videoHeight) {
      // Calcular el aspect ratio real del video
      const aspectRatio = this.videoWidth / this.videoHeight;

      // Redondear a 2 decimales para evitar números muy largos
      const roundedRatio = Math.round(aspectRatio * 100) / 100;

      return `${this.videoWidth} / ${this.videoHeight}`;
    }

    // Fallback a aspect ratios estándar si no hay dimensiones
    return this.isPortrait ? '9 / 16' : '16 / 9';
  }

  /**
   * Inicializa las dimensiones del video basadas en el nodo
   */
  private initializeVideoDimensions() {
    // Si ya tenemos dimensiones guardadas en el nodo, usarlas
    if (this.node.attrs.width && this.node.attrs.height) {
      this.videoWidth = this.node.attrs.width;
      this.videoHeight = this.node.attrs.height;

      // Calcular isPortrait dinámicamente basado en las dimensiones reales
      this.isPortrait = this.videoHeight > this.videoWidth;
    } else {
      // Usar dimensiones por defecto y calcular isPortrait
      // Primero intentar usar el valor guardado, sino calcular dinámicamente
      this.isPortrait = this.node.attrs.isPortrait || false;

      if (this.isPortrait) {
        // Video portrait: ancho máximo 320px
        this.videoWidth = 320;
        this.videoHeight = this.videoWidth * (9 / 16); // Aspect ratio 9:16
      } else {
        // Video landscape: ancho máximo 640px
        this.videoWidth = 640;
        this.videoHeight = this.videoWidth / (16 / 9); // Aspect ratio 16:9
      }
    }
  }

  /**
   * Actualiza el nodo cuando cambia su contenido
   */
  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) {
      return false;
    }

    this.node = node;
    this.renderContent();

    return true;
  }

  /**
   * Renderiza el contenido del video según el proveedor
   */
  renderContent() {
    const { src, provider, media_file_id } = this.node.attrs;

    // Solo crear root si no existe (evitar pestañeos)
    if (!this.root) {
      this.root = createRoot(this.reactContainer);
    }

    switch (provider) {
      case "youtube":
        this.renderYouTubePreview(src);
        break;
      case "vimeo":
        this.renderVimeoPreview(src);
        break;
      default:
        this.renderLocalVideoPreview(src, media_file_id);
        break;
    }
  }

  /**
   * Renderiza preview de YouTube
   */
  private renderYouTubePreview(src: string) {
    const youtubeSrc = getYouTubeUrl(src);

    // // Aplicar dimensiones del nodo al contenedor
    // this.reactContainer.style.width = `${this.videoWidth}px`;
    // this.reactContainer.style.height = this.isPortrait ? 'auto' : `${this.videoHeight}px`;
    // // this.reactContainer.style.aspectRatio = this.getDynamicAspectRatio();

    this.root!.render(
      // <VideoTransitionWrapper delay={200}>
      <iframe
        src={`${youtubeSrc}?rel=0&modestbranding=1&controls=1&disablekb=1&fs=0&iv_load_policy=3&showinfo=0`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          display: "block",
          border: "none",
          maxWidth: "100%",
          width: "100%",
          height: "100%",
          aspectRatio: "16/9",
        }}
      />
      // </VideoTransitionWrapper>
    );
  }

  /**
   * Renderiza preview de Vimeo
   */
  private renderVimeoPreview(src: string) {
    // Aplicar dimensiones del nodo al contenedor
    this.reactContainer.style.width = `${this.videoWidth}px`;
    // this.reactContainer.style.height = this.isPortrait ? 'auto' : `${this.videoHeight}px`;
    //this.reactContainer.style.aspectRatio = this.getDynamicAspectRatio();

    this.root!.render(
      // <VideoTransitionWrapper delay={100}>
      <VimeoVideo
        url={src}
        showLink={false}
        isPreview={true}
      />
      // </VideoTransitionWrapper>
    );
  }

  /**
   * Renderiza preview de video local
   */
  private renderLocalVideoPreview(src: string, media_file_id: number) {
    const mediaFile = this.mediaFilesMap?.[media_file_id];
    const finalSrc = mediaFile && mediaFile?.compressed ? mediaFile.signed_url : src;


    // Aplicar dimensiones del nodo al contenedor (igual que los otros players)
    this.reactContainer.style.width = `${this.videoWidth}px`;
    this.reactContainer.style.height = this.isPortrait ? 'auto' : `${this.videoHeight}px`;
    //this.reactContainer.style.aspectRatio = this.getDynamicAspectRatio();


    // Calcular tasa de optimización si está comprimido
    const optimizationRate = mediaFile?.compressed && mediaFile?.compressed_size && mediaFile?.size
      ? 100 - Math.round((mediaFile.compressed_size / mediaFile.size) * 100)
      : 0;

    this.root!.render(
      // <VideoTransitionWrapper delay={100}>
      <BasicVideoPlayer
        videoUrl={finalSrc}
        mode="preview"
        editable={false}
        blockDownload={this.blockFilesDownload}
        isOwner={this.isOwner}
        canBeCompressed={!!mediaFile?.can_be_compressed}
        isCompressed={!!mediaFile?.compressed}
        optimizationRate={optimizationRate}
      />
      // </VideoTransitionWrapper>
    );
  }

  /**
   * Limpia recursos al destruir el nodo
   */
  destroy() {
    if (this.root) {
      this.root.unmount();
    }
  }
}

// ============================================================================
// CUSTOM VIDEO EXTENSION
// ============================================================================

/**
 * Extensión de Remirror para manejar nodos de video personalizados
 * Soporta videos de YouTube, Vimeo y archivos locales
 */
export class CustomVideoExtension extends NodeExtension {

  constructor(
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    super();
  }

  /**
   * Nombre de la extensión
   */
  get name() {
    return 'customVideo';
  }

  /**
   * Define la especificación del nodo
   */
  override createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      inline: false,
      group: 'block',
      atom: true,
      selectable: true,
      draggable: true,
      attrs: {
        id: { default: null },
        src: { default: '' },
        filename: { default: null },
        filesize: { default: null },
        provider: { default: 'local' },
        "data-uploading": { default: null },
        "data-progress": { default: null },
        media_file_id: { default: null },
        width: { default: 640 },
        height: { default: 360 },
        isPortrait: { default: false },
        ...extra.defaults(),
      },
      parseDOM: [{
        tag: 'div[data-video-src]',
        getAttrs: (dom: HTMLElement) => ({
          src: dom.getAttribute('data-video-src') || '',
          provider: dom.getAttribute('data-video-provider') || 'local',
          "data-uploading": dom.getAttribute('data-uploading') || null,
          "data-progress": dom.getAttribute('data-progress') || null,
          media_file_id: dom.getAttribute('media_file_id') || null,
          width: parseInt(dom.getAttribute('data-width') || '640'),
          height: parseInt(dom.getAttribute('data-height') || '360'),
          isPortrait: dom.getAttribute('data-portrait') === 'true',
          ...extra.parse(dom),
        }),
      }],
      toDOM: (node) => {
        const {
          src,
          provider,
          "data-uploading": uploading,
          "data-progress": progress,
          width,
          height,
          isPortrait,
        } = node.attrs;

        return [
          'div',
          {
            class: 'remirror-video-wrapper',
            'data-video-src': src,
            'data-video-provider': provider,
            'data-uploading': uploading ?? undefined,
            'data-progress': progress ?? undefined,
            'data-width': width,
            'data-height': height,
            'data-portrait': isPortrait,
            style: `width: ${width}px; height: ${height}px;`,
            ...extra.dom(node),
          }
        ] as any;
      },
    };
  }

  /**
   * Define los comandos disponibles para esta extensión
   */
  override createCommands() {
    return {
      insertVideo: (options: {
        id?: string;
        src: string;
        filename?: string;
        filesize?: number;
        provider?: 'local' | 'youtube' | 'vimeo';
        uploading?: boolean;
        progress?: number | null;
      }): CommandFunction => {
        return ({ tr, dispatch }) => {
          const nodeType = this.type;

          const node = nodeType.create({
            src: options.src,
            provider: options.provider || 'local',
            "data-uploading": options.uploading ?? false,
            "data-progress": options.progress ?? null,
            filename: options.filename ?? null,
            filesize: options.filesize ?? null,
            id: options.id ?? null,
          });

          const transaction = tr.replaceSelectionWith(node).scrollIntoView();
          if (dispatch) dispatch(transaction);
          return true;
        };
      },
    };
  }

  /**
   * Crea las vistas de nodo según el modo (edición o preview)
   */
  override createNodeViews() {
    return (node, view, getPos) => {
      if (!view.editable) {
        // Modo preview (solo lectura)
        return new PreviewVideoNodeView(
          node,
          view,
          this.mediaFilesMap,
          this.isOwner,
          this.blockFilesDownload
        );
      }

      // Modo edición
      return new CustomVideoNodeView(
        node,
        view,
        getPos!,
        this.mediaFilesMap
      );
    };
  }
}
